import classNames from 'classnames';
import { PopoverPanel, PopoverItem, Button } from '@crazyegginc/hatch';

import { useAuthContext, useModal } from '/src/hooks';
import { useAllowedCriterias } from '../useAllowedCriterias';
import { AUDIENCE_METADATA, CATEGORIES } from '../metadata';
import { AUDIENCE_LEVELS, AUDIENCE_MODES, UPGRADE_REASONS } from '../constants';
import { AudienceUpsellModal } from './modals/AudienceUpsellModal';

import { ReactComponent as UpsellIcon } from '@crazyegginc/hatch/dist/images/icon-crown-filled.svg';
import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-expand.svg';

export function ConditionsPopover({
  anchor = 'top end',
  className,
  mode = AUDIENCE_MODES.TARGETING,
  onClick,
  onUpsellClick,
}) {
  const { allowedTargetingCriterias, allowedFilteringCriterias } = useAllowedCriterias();
  const { capabilities } = useAuthContext();
  const allowedCriterias = mode === AUDIENCE_MODES.FILTERING ? allowedFilteringCriterias : allowedTargetingCriterias;
  const shouldShowUpsell =
    mode === AUDIENCE_MODES.TARGETING && capabilities.audiences.allowedTargetingCriteria.level !== AUDIENCE_LEVELS.ALL;
  const modal = useModal();

  function getUpsellModal(criteriaAccess) {
    return criteriaAccess?.reason === UPGRADE_REASONS.FEATURE_UPGRADE && criteriaAccess.upsellModal
      ? criteriaAccess.upsellModal
      : AudienceUpsellModal;
  }

  function showUpsell(criteriaAccess) {
    const UpsellModal = getUpsellModal(criteriaAccess);

    if (onUpsellClick) {
      onUpsellClick(UpsellModal);
      return;
    }

    modal.show(<UpsellModal />);
  }

  return (
    <PopoverPanel
      anchor={{ to: anchor, gap: '4px' }}
      data-testid="audience-conditions-popover"
      className={classNames(
        'flex flex-col rounded-[10px] w-[500px] h-[450px] bg-white border-mystic-500 shadow-md border z-[100001]',
        className,
      )}
    >
      {({ close }) => (
        <>
          {shouldShowUpsell ? (
            <Button
              size="lg"
              variant="secondary"
              className="h-10 rounded flex items-center shrink-0 mt-2.5 mb-3.75 mx-2.5 px-5 justify-between"
              onClick={() => {
                close();
                showUpsell();
              }}
            >
              <div className="flex items-center">
                <UpsellIcon className="h-3.75 w-3.75 fill-current text-[#fbbd33] mr-2.5" />
                <span>Upgrade for Full Audience Targeting</span>
              </div>
              <ArrowIcon className="ml-2.5 h-2.5 w-2.5 rotate-90 fill-current" />
            </Button>
          ) : null}
          <div
            className={classNames(
              'flex overflow-y-auto',
              'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-cadet-blue-500 scrollbar-thumb-rounded',
              {
                'pt-3.75': shouldShowUpsell,
                'pt-10': !shouldShowUpsell,
              },
            )}
          >
            <div className="w-1/2 grow pl-[30px] space-y-10">
              <Category
                title={CATEGORIES.VISITOR_INFO}
                allowedCriterias={allowedCriterias}
                onClick={onClick}
                onUpsellClick={showUpsell}
                close={close}
              />
              <Category
                title={CATEGORIES.PATH}
                allowedCriterias={allowedCriterias}
                onClick={onClick}
                onUpsellClick={showUpsell}
                close={close}
              />
            </div>
            <div className="w-1/2 grow pl-[30px] space-y-10">
              <Category
                title={CATEGORIES.SESSION_INFO}
                allowedCriterias={allowedCriterias}
                onClick={onClick}
                onUpsellClick={showUpsell}
                close={close}
              />
              <Category
                title={CATEGORIES.ACTIVITY}
                allowedCriterias={allowedCriterias}
                onClick={onClick}
                onUpsellClick={showUpsell}
                close={close}
              />
              <Category
                title={CATEGORIES.AD_CAMPAIGN}
                allowedCriterias={allowedCriterias}
                onClick={onClick}
                onUpsellClick={showUpsell}
                close={close}
              />
            </div>
          </div>
        </>
      )}
    </PopoverPanel>
  );
}

function Category({ title, allowedCriterias, onClick, onUpsellClick, close }) {
  return (
    <section className="last:pb-[30px]">
      <h3 className="text-body-1 leading-none mb-5 pl-2.5">{title}</h3>
      <div className="space-y-1.25">
        {Object.entries(AUDIENCE_METADATA)
          .filter(([, { category }]) => category === title)
          .map(([key, { icon: Icon, label, secondaryLabel }]) => {
            const criteriaAccess = allowedCriterias[key];
            const needsUpgrade = !criteriaAccess?.allowed;

            return (
              <PopoverItem
                key={key}
                className="flex items-center space-x-2.5 group"
                close={true}
                onClick={() => {
                  if (needsUpgrade) {
                    close();
                    onUpsellClick(criteriaAccess);
                  } else {
                    onClick(key);
                  }
                }}
              >
                {needsUpgrade ? (
                  <UpsellIcon className="h-3.75 w-3.75 fill-current text-[#fbbd33]" />
                ) : (
                  <Icon className="h-3.75 w-3.75 fill-current group-hover:text-black-pearl-500 text-cadet-blue-500" />
                )}

                <span
                  className={classNames('', {
                    '!text-lynch-500': needsUpgrade,
                    '!text-black-pearl-500': !needsUpgrade,
                  })}
                >
                  <span>{label}</span>
                  {secondaryLabel ? <span className="text-xs whitespace-pre">{` ${secondaryLabel}`}</span> : null}
                </span>
              </PopoverItem>
            );
          })}
      </div>
    </section>
  );
}
